import { z } from 'zod'
import { salesOrderApi } from './api/sales-order-api'
import type { CustomerDisplaySyncRequest } from './schemas/customer-display-event-schemas'

export function createDisplaySyncAckSchema() {
    return z.object({
        success: z.literal(true),
        revision: z.number().nonnegative().refine(Number.isInteger).optional(),
        expires_at: z.string().nullable().optional(),
    })
}
type PublishResult =
    { status: 'synced'; revision?: number } | { status: 'superseded' }
type Sender = (
    request: CustomerDisplaySyncRequest,
) => Promise<{ data?: unknown }>
type Channel = {
    tail: Promise<void>
    generation: number
    checkoutGeneration?: number
    revision?: number
    type?: CustomerDisplaySyncRequest['type']
}

/** Serializes local intents; server revisions also protect against another publisher. */
export function createPosDisplayPublisher(send: Sender) {
    const channels = new Map<string, Channel>()
    function publish(
        request: CustomerDisplaySyncRequest,
        options: { replace?: boolean } = {},
    ): Promise<PublishResult> {
        let channel = channels.get(request.display_user_id)
        if (!channel) {
            channel = { tail: Promise.resolve(), generation: 0 }
            channels.set(request.display_user_id, channel)
        }
        const current = channel
        const generation = ++current.generation
        current.type = request.type
        if (request.type === 'checkout') current.checkoutGeneration = generation
        const operation = current.tail.then(
            async (): Promise<PublishResult> => {
                if (generation !== current.generation)
                    return { status: 'superseded' }
                const expected =
                    request.expected_revision ??
                    (options.replace ? undefined : current.revision)
                const payload =
                    expected === undefined
                        ? request
                        : { ...request, expected_revision: expected }
                const response = await send(payload)
                const ack = createDisplaySyncAckSchema().parse(response.data)
                current.revision = ack.revision
                if (request.type === 'checkout') {
                    // Old displays rely on the clear event; upgraded servers enforce its revision.
                    setTimeout(() => {
                        if (generation !== current.generation) return
                        void publish({
                            display_user_id: request.display_user_id,
                            type: 'clear',
                            ...(ack.revision === undefined
                                ? {}
                                : { expected_revision: ack.revision }),
                        }).catch(() => {})
                    }, 3_000)
                }
                return { status: 'synced', revision: ack.revision }
            },
        )
        current.tail = operation.then(
            () => {},
            () => {},
        )
        return operation
    }
    return {
        publish,
        getCheckoutGeneration: (displayID: string) =>
            channels.get(displayID)?.checkoutGeneration ?? 0,
        isCheckout: (displayID: string) =>
            channels.get(displayID)?.type === 'checkout',
    }
}

export const posDisplayPublisher = createPosDisplayPublisher((request) =>
    salesOrderApi.displaySync(request),
)
